import React, { useState, useCallback, useMemo, memo, useEffect, useRef } from 'react';
import api from '../../services/api';
import { Team } from '../../types';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import './Predictor.css';

// Define types
interface PredictionFactors {
  [key: string]: number | string | null;
}

interface RecentForm {
  form_string: string;
  avg_goals_scored: number;
  avg_goals_conceded: number;
  win_rate?: number;
}

interface TeamStats {
  position?: number;
  played?: number;
  won?: number;
  drawn?: number;
  lost?: number;
  goals_for?: number;
  goals_against?: number;
  goal_difference?: number;
  points?: number;
  form?: string;
  league?: string;
  season?: string;
}

interface Prediction {
  home_win_prob: number;
  draw_prob: number;
  away_win_prob: number;
  confidence: number;
  predicted_home_score: number;
  predicted_away_score: number;
  explanation?: string;
  factors?: PredictionFactors;
  model_version?: string;
  processing_time_ms?: number;
  most_likely_outcome?: string;
  data_source?: string;
  match_date?: string;
  match_time?: string;
  match_datetime?: string;
  match_day?: string;
  match_day_date?: string;
  match_time_formatted?: string;
  is_actual_scheduled_match?: boolean;
  competition?: string;
  match_id?: number;
  recent_form?: {
    home: RecentForm;
    away: RecentForm;
  };
  home_stats?: TeamStats;
  away_stats?: TeamStats;
}

interface ApiResponse {
  status: string;
  prediction: Prediction;
  data_source?: string;
  match_date?: string;
  match_time?: string;
  match_datetime?: string;
  match_day?: string;
  match_day_date?: string;
  match_time_formatted?: string;
  is_actual_scheduled_match?: boolean;
  competition?: string;
  match_id?: number;
  home_team?: string;
  away_team?: string;
  message?: string;
}

interface TooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number }>;
}

// Constants
const COLORS = ['#4caf50', '#ff9800', '#f44336'];
const DEBOUNCE_DELAY = 300;

// Complete list of popular football teams for autocomplete
const POPULAR_TEAMS = [
  // Premier League
  'Arsenal', 'Aston Villa', 'Bournemouth', 'Brentford', 'Brighton', 
  'Chelsea', 'Crystal Palace', 'Everton', 'Fulham', 'Ipswich Town',
  'Leicester City', 'Liverpool', 'Manchester City', 'Manchester United',
  'Newcastle United', 'Nottingham Forest', 'Southampton', 'Tottenham Hotspur',
  'West Ham United', 'Wolverhampton Wanderers',
  
  // La Liga
  'Real Madrid', 'Barcelona', 'Atletico Madrid', 'Sevilla', 'Real Sociedad',
  'Athletic Bilbao', 'Valencia', 'Villarreal', 'Betis', 'Getafe',
  
  // Bundesliga
  'Bayern Munich', 'Borussia Dortmund', 'RB Leipzig', 'Bayer Leverkusen',
  'Eintracht Frankfurt', 'Borussia Monchengladbach', 'Wolfsburg', 'Stuttgart',
  
  // Serie A
  'Juventus', 'Inter Milan', 'AC Milan', 'Napoli', 'Roma', 'Lazio',
  'Atalanta', 'Fiorentina', 'Torino', 'Bologna',
  
  // Ligue 1
  'Paris Saint-Germain', 'Marseille', 'Lyon', 'Monaco', 'Lille',
  'Nice', 'Rennes', 'Lens', 'Strasbourg',
  
  // Other European
  'Ajax', 'PSV', 'Feyenoord', 'Benfica', 'Porto', 'Sporting CP',
  'Celtic', 'Rangers', 'Galatasaray', 'Fenerbahce', 'Shakhtar Donetsk',
  'Dinamo Zagreb', 'Red Star Belgrade', 'Slavia Prague', 'Young Boys',
  'Club Brugge', 'Anderlecht', 'FC Copenhagen', 'Rosenborg'
].sort();

// Helper function to format date for display in the format "18/03/2026 17:45"
const formatMatchDateTime = (dateTimeString: string): string => {
  const date = new Date(dateTimeString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

// Helper function to format date from API response
const formatMatchInfo = (prediction: Prediction): string => {
  console.log('Formatting match info from prediction:', prediction);
  
  // If the API returns match_datetime (e.g., "2026-03-18T17:45:00Z")
  if (prediction.match_datetime) {
    return formatMatchDateTime(prediction.match_datetime);
  } 
  
  // If the API returns separate date and time
  else if (prediction.match_date && prediction.match_time) {
    // Construct a proper datetime string
    const dateStr = prediction.match_date;
    const timeStr = prediction.match_time;
    
    // Handle different time formats
    let formattedTime = timeStr;
    if (timeStr.length === 4) {
      // If time is like "1745" (HHMM format)
      formattedTime = `${timeStr.substring(0, 2)}:${timeStr.substring(2, 4)}`;
    }
    
    return `${dateStr} ${formattedTime}`;
  }
  
  // If the API returns pre-formatted fields
  else if (prediction.match_day && prediction.match_day_date && prediction.match_time_formatted) {
    return `${prediction.match_day_date}/${new Date().getFullYear()} ${prediction.match_time_formatted}`;
  }
  
  return '';
};

// Memoized helper functions
const getProbValue = (value: number | null | undefined): number => {
  if (value === null || value === undefined) return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

const getConfidenceColor = (confidence: number | null | undefined): string => {
  const conf = getProbValue(confidence);
  if (conf >= 0.7) return '#2e7d32';
  if (conf >= 0.5) return '#ed6c02';
  return '#d32f2f';
};

const getConfidenceText = (confidence: number | null | undefined): string => {
  const conf = getProbValue(confidence);
  if (conf >= 0.8) return 'Very High';
  if (conf >= 0.6) return 'High';
  if (conf >= 0.4) return 'Medium';
  if (conf >= 0.2) return 'Low';
  return 'Very Low';
};

const formatFactorValue = (value: number | string | null): string => {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'number') {
    return value.toFixed(2);
  }
  return String(value);
};

// Memoized Custom Tooltip component
const CustomTooltip = memo<{ active?: boolean; payload?: any[] }>(({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="tooltip-label">{payload[0].name}</p>
        <p className="tooltip-value">{Number(payload[0].value).toFixed(1)}%</p>
      </div>
    );
  }
  return null;
});

CustomTooltip.displayName = 'CustomTooltip';

// Memoized Pie Chart component
const ProbabilityPieChart = memo<{ data: Array<{ name: string; value: number }> }>(({ data }) => {
  const renderCustomizedLabel = useCallback((props: any): string => {
    const { percent } = props;
    if (typeof percent === 'number' && percent > 0.05) {
      return `${(percent * 100).toFixed(0)}%`;
    }
    return '';
  }, []);

  const filteredData = useMemo(() => data.filter(item => item.value > 0), [data]);

  if (filteredData.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie
          data={filteredData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={renderCustomizedLabel}
          outerRadius={120}
          dataKey="value"
          isAnimationActive={true}
          animationDuration={500}
        >
          {filteredData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip content={<CustomTooltip />} />
      </PieChart>
    </ResponsiveContainer>
  );
});

ProbabilityPieChart.displayName = 'ProbabilityPieChart';

// Recent Form Display Component
const RecentFormDisplay: React.FC<{ formString: string }> = ({ formString }) => {
  if (!formString || formString === 'N/A') return <span className="form-na">No recent data</span>;
  
  return (
    <div className="recent-form">
      {formString.split('').map((result, index) => (
        <span
          key={index}
          className={`form-badge ${result === 'W' ? 'win' : result === 'D' ? 'draw' : 'loss'}`}
          title={result === 'W' ? 'Win' : result === 'D' ? 'Draw' : 'Loss'}
        >
          {result}
        </span>
      ))}
    </div>
  );
};

// Team Stats Table Component
const TeamStatsTable: React.FC<{ stats?: TeamStats; teamName: string }> = ({ stats, teamName }) => {
  if (!stats) {
    return (
      <div className="stats-table-container no-data">
        <h4 className="stats-table-title">{teamName}</h4>
        <p className="no-stats-message">No statistics available</p>
      </div>
    );
  }
  
  return (
    <div className="stats-table-container">
      <h4 className="stats-table-title">{teamName}</h4>
      <table className="stats-table">
        <tbody>
          <tr>
            <td className="stats-label">Position</td>
            <td className="stats-value">{stats.position || '-'}</td>
            <td className="stats-label">Played</td>
            <td className="stats-value">{stats.played || '-'}</td>
          </tr>
          <tr>
            <td className="stats-label">Won</td>
            <td className="stats-value">{stats.won || '-'}</td>
            <td className="stats-label">Drawn</td>
            <td className="stats-value">{stats.drawn || '-'}</td>
          </tr>
          <tr>
            <td className="stats-label">Lost</td>
            <td className="stats-value">{stats.lost || '-'}</td>
            <td className="stats-label">Goals For</td>
            <td className="stats-value">{stats.goals_for || '-'}</td>
          </tr>
          <tr>
            <td className="stats-label">Goals Against</td>
            <td className="stats-value">{stats.goals_against || '-'}</td>
            <td className="stats-label">Goal Diff</td>
            <td className={`stats-value ${stats.goal_difference && stats.goal_difference > 0 ? 'positive' : stats.goal_difference && stats.goal_difference < 0 ? 'negative' : ''}`}>
              {stats.goal_difference ? (stats.goal_difference > 0 ? `+${stats.goal_difference}` : stats.goal_difference) : '-'}
            </td>
          </tr>
          <tr>
            <td className="stats-label">Points</td>
            <td className="stats-value points" colSpan={3}>{stats.points || '-'}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

// Autocomplete Item Component
const AutocompleteItem: React.FC<{ 
  team: string; 
  isHighlighted: boolean; 
  onClick: () => void;
  onMouseEnter: () => void;
}> = ({ team, isHighlighted, onClick, onMouseEnter }) => (
  <div
    className={`autocomplete-item ${isHighlighted ? 'highlighted' : ''}`}
    onClick={onClick}
    onMouseEnter={onMouseEnter}
  >
    <span className="team-name">{team}</span>
    <span className="team-icon">⚽</span>
  </div>
);

// Main Component
const Predictor: React.FC = () => {
  const [homeTeam, setHomeTeam] = useState<string>('');
  const [awayTeam, setAwayTeam] = useState<string>('');
  const [homeSuggestions, setHomeSuggestions] = useState<string[]>([]);
  const [awaySuggestions, setAwaySuggestions] = useState<string[]>([]);
  const [showHomeSuggestions, setShowHomeSuggestions] = useState<boolean>(false);
  const [showAwaySuggestions, setShowAwaySuggestions] = useState<boolean>(false);
  const [homeHighlightedIndex, setHomeHighlightedIndex] = useState<number>(-1);
  const [awayHighlightedIndex, setAwayHighlightedIndex] = useState<number>(-1);
  
  const [prediction, setPrediction] = useState<Prediction | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<string>('');

  const homeInputRef = useRef<HTMLInputElement>(null);
  const awayInputRef = useRef<HTMLInputElement>(null);
  const homeSuggestionsRef = useRef<HTMLDivElement>(null);
  const awaySuggestionsRef = useRef<HTMLDivElement>(null);

  // Filter suggestions based on input
  const getSuggestions = useCallback((input: string): string[] => {
    if (!input || input.length < 1) return [];
    const searchTerm = input.toLowerCase();
    return POPULAR_TEAMS
      .filter(team => team.toLowerCase().includes(searchTerm))
      .slice(0, 10); // Limit to 10 suggestions
  }, []);

  // Handle home team input change
  const handleHomeInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setHomeTeam(value);
    
    if (value.length >= 1) {
      const suggestions = getSuggestions(value);
      setHomeSuggestions(suggestions);
      setShowHomeSuggestions(suggestions.length > 0);
      setHomeHighlightedIndex(-1);
    } else {
      setHomeSuggestions([]);
      setShowHomeSuggestions(false);
    }
  }, [getSuggestions]);

  // Handle away team input change
  const handleAwayInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAwayTeam(value);
    
    if (value.length >= 1) {
      const suggestions = getSuggestions(value);
      setAwaySuggestions(suggestions);
      setShowAwaySuggestions(suggestions.length > 0);
      setAwayHighlightedIndex(-1);
    } else {
      setAwaySuggestions([]);
      setShowAwaySuggestions(false);
    }
  }, [getSuggestions]);

  // Handle home team selection
  const handleHomeSelect = useCallback((team: string) => {
    setHomeTeam(team);
    setHomeSuggestions([]);
    setShowHomeSuggestions(false);
    setHomeHighlightedIndex(-1);
    // Move focus to away input after selection
    awayInputRef.current?.focus();
  }, []);

  // Handle away team selection
  const handleAwaySelect = useCallback((team: string) => {
    setAwayTeam(team);
    setAwaySuggestions([]);
    setShowAwaySuggestions(false);
    setAwayHighlightedIndex(-1);
  }, []);

  // Handle keyboard navigation for home suggestions
  const handleHomeKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showHomeSuggestions || homeSuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHomeHighlightedIndex(prev => 
          prev < homeSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHomeHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (homeHighlightedIndex >= 0) {
          handleHomeSelect(homeSuggestions[homeHighlightedIndex]);
        }
        break;
      case 'Escape':
        setShowHomeSuggestions(false);
        setHomeHighlightedIndex(-1);
        break;
      case 'Tab':
        setShowHomeSuggestions(false);
        break;
    }
  }, [showHomeSuggestions, homeSuggestions, homeHighlightedIndex, handleHomeSelect]);

  // Handle keyboard navigation for away suggestions
  const handleAwayKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showAwaySuggestions || awaySuggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setAwayHighlightedIndex(prev => 
          prev < awaySuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setAwayHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (awayHighlightedIndex >= 0) {
          handleAwaySelect(awaySuggestions[awayHighlightedIndex]);
        }
        break;
      case 'Escape':
        setShowAwaySuggestions(false);
        setAwayHighlightedIndex(-1);
        break;
      case 'Tab':
        setShowAwaySuggestions(false);
        break;
    }
  }, [showAwaySuggestions, awaySuggestions, awayHighlightedIndex, handleAwaySelect]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (homeSuggestionsRef.current && !homeSuggestionsRef.current.contains(event.target as Node) &&
          homeInputRef.current && !homeInputRef.current.contains(event.target as Node)) {
        setShowHomeSuggestions(false);
      }
      if (awaySuggestionsRef.current && !awaySuggestionsRef.current.contains(event.target as Node) &&
          awayInputRef.current && !awayInputRef.current.contains(event.target as Node)) {
        setShowAwaySuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (homeHighlightedIndex >= 0 && homeSuggestionsRef.current) {
      const highlightedElement = homeSuggestionsRef.current.children[homeHighlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [homeHighlightedIndex]);

  useEffect(() => {
    if (awayHighlightedIndex >= 0 && awaySuggestionsRef.current) {
      const highlightedElement = awaySuggestionsRef.current.children[awayHighlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [awayHighlightedIndex]);

  // Memoized prediction handler - uses ONLY real data endpoints
  const handlePredict = useCallback(async () => {
    // Validation
    if (!homeTeam.trim() || !awayTeam.trim()) {
      setError('Please select both teams');
      return;
    }

    if (homeTeam.trim().toLowerCase() === awayTeam.trim().toLowerCase()) {
      setError('Teams must be different');
      return;
    }

    setLoading(true);
    setError(null);
    setPrediction(null);
    setDataSource('');
    
    // Try fast cached endpoint first, then fallback to fresh data
    const endpoints = [
      { url: '/predict-fast/', timeout: 5000, name: 'cached' },  // Fast cached real data
      { url: '/predict/', timeout: 8000, name: 'live' }          // Fresh real data
    ];

    let lastError: any = null;

    for (const endpoint of endpoints) {
      try {
        console.log(`Trying ${endpoint.name} real data endpoint...`);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), endpoint.timeout);

        const response = await api.post<ApiResponse>(endpoint.url, {
          home_team: homeTeam.trim(),
          away_team: awayTeam.trim(),
          include_explanation: true
        }, {
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        
        console.log(`${endpoint.name} response data:`, response.data);
        console.log(`${endpoint.name} prediction:`, response.data.prediction);
        console.log(`${endpoint.name} match_date:`, response.data.prediction?.match_date);
        console.log(`${endpoint.name} match_time:`, response.data.prediction?.match_time);
        console.log(`${endpoint.name} match_datetime:`, response.data.prediction?.match_datetime);
        console.log(`${endpoint.name} home_stats:`, response.data.prediction?.home_stats);
        console.log(`${endpoint.name} away_stats:`, response.data.prediction?.away_stats);
        
        // Check if response has the expected structure
        if (response.data && response.data.status === 'success' && response.data.prediction) {
          setPrediction(response.data.prediction);
          setDataSource(response.data.data_source || endpoint.name);
          setLoading(false);
          return; // Success, exit function
        } else {
          console.warn(`Unexpected response format from ${endpoint.name}:`, response.data);
          lastError = new Error('Unexpected response format');
        }

      } catch (err: any) {
        console.warn(`${endpoint.name} endpoint failed:`, err.message);
        lastError = err;
        // Continue to next endpoint
      }
    }

    // All endpoints failed
    console.error('All prediction attempts failed:', lastError);
    setError(lastError?.response?.data?.message || lastError?.message || 'Prediction failed. Please try again.');
    setLoading(false);

  }, [homeTeam, awayTeam]);

  // Memoized computed values
  const probabilities = useMemo(() => {
    if (!prediction) return { home: 0, draw: 0, away: 0, confidence: 0 };
    return {
      home: getProbValue(prediction.home_win_prob),
      draw: getProbValue(prediction.draw_prob),
      away: getProbValue(prediction.away_win_prob),
      confidence: getProbValue(prediction.confidence)
    };
  }, [prediction]);

  const pieData = useMemo(() => {
    if (!prediction) return [];
    return [
      { name: `${homeTeam} Win`, value: probabilities.home * 100 },
      { name: 'Draw', value: probabilities.draw * 100 },
      { name: `${awayTeam} Win`, value: probabilities.away * 100 },
    ];
  }, [prediction, homeTeam, awayTeam, probabilities]);

  const confidenceColor = useMemo(() => 
    getConfidenceColor(probabilities.confidence), 
    [probabilities.confidence]
  );

  const confidenceText = useMemo(() => 
    getConfidenceText(probabilities.confidence),
    [probabilities.confidence]
  );

  // Determine winner
  const winner = useMemo(() => {
    if (!prediction) return null;
    if (probabilities.home > probabilities.draw && probabilities.home > probabilities.away) {
      return { team: homeTeam, probability: probabilities.home };
    } else if (probabilities.away > probabilities.home && probabilities.away > probabilities.draw) {
      return { team: awayTeam, probability: probabilities.away };
    } else if (probabilities.draw > probabilities.home && probabilities.draw > probabilities.away) {
      return { team: 'Draw', probability: probabilities.draw };
    }
    return null;
  }, [prediction, homeTeam, awayTeam, probabilities]);

  // Get formatted match info
  const formattedMatchInfo = useMemo(() => {
    if (!prediction) return '';
    return formatMatchInfo(prediction);
  }, [prediction]);

  return (
    <div className="predictor-container">
      <div className="paper">
        <div className="header">
          <h1 className="title">
            ⚽ ZiTech Football Predictor
          </h1>
          {(homeTeam || awayTeam) && (
            <button className="clear-btn" onClick={() => {
              setHomeTeam('');
              setAwayTeam('');
              setPrediction(null);
              setError(null);
            }} title="Clear all">
              ✕ Clear
            </button>
          )}
        </div>

        {/* Team Selection */}
        <div className="team-selection">
          <div className="team-select-wrapper">
            <div className="autocomplete-wrapper">
              <label className="input-label" htmlFor="home-team">Home Team</label>
              <div className="input-container">
                <input
                  ref={homeInputRef}
                  id="home-team"
                  type="text"
                  className={`team-input ${!homeTeam && error ? 'error' : ''}`}
                  placeholder="Type team name..."
                  value={homeTeam}
                  onChange={handleHomeInputChange}
                  onKeyDown={handleHomeKeyDown}
                  onFocus={() => {
                    if (homeSuggestions.length > 0) setShowHomeSuggestions(true);
                  }}
                  autoComplete="off"
                  spellCheck="false"
                />
                {homeTeam && (
                  <button 
                    className="clear-input-btn" 
                    onClick={() => {
                      setHomeTeam('');
                      setHomeSuggestions([]);
                      homeInputRef.current?.focus();
                    }}
                    type="button"
                  >
                    ✕
                  </button>
                )}
              </div>
              {!homeTeam && error && (
                <span className="error-text">Home team is required</span>
              )}
              
              {/* Home Team Autocomplete Dropdown */}
              {showHomeSuggestions && homeSuggestions.length > 0 && (
                <div 
                  ref={homeSuggestionsRef}
                  className="autocomplete-dropdown"
                >
                  {homeSuggestions.map((team, index) => (
                    <AutocompleteItem
                      key={team}
                      team={team}
                      isHighlighted={index === homeHighlightedIndex}
                      onClick={() => handleHomeSelect(team)}
                      onMouseEnter={() => setHomeHighlightedIndex(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="vs-container">
            <button className="swap-btn" onClick={() => {
              setHomeTeam(awayTeam);
              setAwayTeam(homeTeam);
              setPrediction(null);
            }} title="Swap teams">
              ⇄
            </button>
            <div className="vs-circle">
              <span className="vs-text">VS</span>
            </div>
          </div>

          <div className="team-select-wrapper">
            <div className="autocomplete-wrapper">
              <label className="input-label" htmlFor="away-team">Away Team</label>
              <div className="input-container">
                <input
                  ref={awayInputRef}
                  id="away-team"
                  type="text"
                  className={`team-input ${!awayTeam && error ? 'error' : ''}`}
                  placeholder="Type team name..."
                  value={awayTeam}
                  onChange={handleAwayInputChange}
                  onKeyDown={handleAwayKeyDown}
                  onFocus={() => {
                    if (awaySuggestions.length > 0) setShowAwaySuggestions(true);
                  }}
                  autoComplete="off"
                  spellCheck="false"
                />
                {awayTeam && (
                  <button 
                    className="clear-input-btn" 
                    onClick={() => {
                      setAwayTeam('');
                      setAwaySuggestions([]);
                      awayInputRef.current?.focus();
                    }}
                    type="button"
                  >
                    ✕
                  </button>
                )}
              </div>
              {!awayTeam && error && (
                <span className="error-text">Away team is required</span>
              )}
              
              {/* Away Team Autocomplete Dropdown */}
              {showAwaySuggestions && awaySuggestions.length > 0 && (
                <div 
                  ref={awaySuggestionsRef}
                  className="autocomplete-dropdown"
                >
                  {awaySuggestions.map((team, index) => (
                    <AutocompleteItem
                      key={team}
                      team={team}
                      isHighlighted={index === awayHighlightedIndex}
                      onClick={() => handleAwaySelect(team)}
                      onMouseEnter={() => setAwayHighlightedIndex(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="button-wrapper">
            <button
              className={`predict-button ${(loading || !homeTeam || !awayTeam) ? 'disabled' : ''}`}
              onClick={handlePredict}
              disabled={loading || !homeTeam || !awayTeam}
            >
              {loading ? (
                <>
                  <div className="spinner" />
                  <span>Fetching real data...</span>
                </>
              ) : (
                'Predict Match'
              )}
            </button>
          </div>

          {error && (
            <div className="alert error" role="alert">
              <span className="alert-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Prediction Results */}
        {prediction && (
          <div className="results">
            <div className="results-header">
              <h2 className="results-title">
                Prediction Results
                {winner && (
                  <span className="winner-badge">
                    {winner.team === 'Draw' ? '🤝' : '🏆'} {winner.team}
                  </span>
                )}
              </h2>
              <div className="results-meta">
                {prediction.processing_time_ms && (
                  <span className="processing-time">
                    ⚡ {prediction.processing_time_ms}ms
                  </span>
                )}
              </div>
            </div>

            {/* Match Info Header */}
            <div className="match-info-header">
              <div className="match-datetime-display">
                <span className="datetime-icon">📅</span>
                <span className="datetime-text">
                  {formattedMatchInfo || 'Date to be confirmed'}
                </span>
                {prediction.is_actual_scheduled_match && (
                  <>
                    <span className="scheduled-badge">Official Fixture</span>
                    {prediction.competition && (
                      <span className="competition-badge">{prediction.competition}</span>
                    )}
                  </>
                )}
              </div>
              <div className="match-teams-display">
                <span className="home-team-display">{homeTeam}</span>
                <span className="vs-display">vs</span>
                <span className="away-team-display">{awayTeam}</span>
              </div>
              {prediction.match_id && (
                <div className="match-id-display">
                  Match ID: {prediction.match_id}
                </div>
              )}
            </div>

            {/* Data Source Indicator */}
            <div className="data-source-badge">
              <span className="source-icon">📡</span>
              <span className="source-text">
                {dataSource === 'cached' ? 'Cached Real Data' : 'Live Real Data'}
              </span>
              <span className="source-api">football-data.org API</span>
            </div>

            <div className="results-grid">
              {/* Win Probabilities Card */}
              <div className="card">
                <div className="card-content">
                  <h3 className="card-title">Win Probabilities</h3>
                  
                  <div className="probability-item">
                    <div className="probability-header">
                      <span className="probability-label">{homeTeam}</span>
                      <span className="probability-value">
                        {(probabilities.home * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill home"
                        style={{ width: `${probabilities.home * 100}%` }}
                      />
                    </div>
                  </div>
                  
                  <div className="probability-item">
                    <div className="probability-header">
                      <span className="probability-label">Draw</span>
                      <span className="probability-value">
                        {(probabilities.draw * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill draw"
                        style={{ width: `${probabilities.draw * 100}%` }}
                      />
                    </div>
                  </div>
                  
                  <div className="probability-item">
                    <div className="probability-header">
                      <span className="probability-label">{awayTeam}</span>
                      <span className="probability-value">
                        {(probabilities.away * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill away"
                        style={{ width: `${probabilities.away * 100}%` }}
                      />
                    </div>
                  </div>

                  <div className="score-section">
                    <h4 className="score-title">Predicted Score</h4>
                    <p className="score-value">
                      {/* Display score based on who is winning */}
                      <span className="home-score">
                        {winner?.team === homeTeam 
                          ? Math.max(prediction.predicted_home_score || 0, prediction.predicted_away_score || 0)
                          : winner?.team === awayTeam
                            ? Math.min(prediction.predicted_home_score || 0, prediction.predicted_away_score || 0)
                            : prediction.predicted_home_score || 0}
                      </span>
                      <span className="score-separator">:</span>
                      <span className="away-score">
                        {winner?.team === awayTeam 
                          ? Math.max(prediction.predicted_home_score || 0, prediction.predicted_away_score || 0)
                          : winner?.team === homeTeam
                            ? Math.min(prediction.predicted_home_score || 0, prediction.predicted_away_score || 0)
                            : prediction.predicted_away_score || 0}
                      </span>
                    </p>
                    <div className="confidence-display">
                      <div 
                        className="confidence-indicator"
                        style={{ backgroundColor: confidenceColor }}
                      />
                      <span className="confidence-text">
                        {confidenceText} Confidence ({(probabilities.confidence * 100).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pie Chart Card */}
              <div className="card">
                <div className="card-content">
                  <h3 className="card-title">Probability Distribution</h3>
                  <div className="chart-container">
                    <ProbabilityPieChart data={pieData} />
                  </div>
                  <div className="chart-legend">
                    {pieData.map((item, index) => (
                      <div key={index} className="legend-item">
                        <span className="legend-color" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                        <span className="legend-label">{item.name}</span>
                        <span className="legend-value">{item.value.toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Recent Form Card */}
              {prediction.recent_form && (
                <div className="card">
                  <div className="card-content">
                    <h3 className="card-title">📋 Recent Form (Last 5)</h3>
                    
                    <div className="form-comparison">
                      <div className="form-team">
                        <span className="form-team-name">{homeTeam}</span>
                        <RecentFormDisplay formString={prediction.recent_form.home.form_string} />
                        <div className="form-stats">
                          <span>GF: {prediction.recent_form.home.avg_goals_scored.toFixed(1)}</span>
                          <span>GA: {prediction.recent_form.home.avg_goals_conceded.toFixed(1)}</span>
                        </div>
                      </div>
                      
                      <div className="form-vs">VS</div>
                      
                      <div className="form-team">
                        <span className="form-team-name">{awayTeam}</span>
                        <RecentFormDisplay formString={prediction.recent_form.away.form_string} />
                        <div className="form-stats">
                          <span>GF: {prediction.recent_form.away.avg_goals_scored.toFixed(1)}</span>
                          <span>GA: {prediction.recent_form.away.avg_goals_conceded.toFixed(1)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Team Statistics Card - Table Format */}
              {(prediction.home_stats || prediction.away_stats) && (
                <div className="card full-width">
                  <div className="card-content">
                    <h3 className="card-title">📊 Season Statistics</h3>
                    <div className="stats-tables-container">
                      {/* Home Team Stats */}
                      <div className="stats-table-wrapper">
                        <TeamStatsTable stats={prediction.home_stats} teamName={homeTeam} />
                      </div>
                      
                      {/* VS Divider */}
                      <div className="stats-vs-container">
                        <div className="stats-vs">VS</div>
                      </div>
                      
                      {/* Away Team Stats */}
                      <div className="stats-table-wrapper">
                        <TeamStatsTable stats={prediction.away_stats} teamName={awayTeam} />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* AI Analysis Card */}
              <div className="card full-width">
                <div className="card-content">
                  <h3 className="card-title">🤖 AI Analysis</h3>
                  <p className="analysis-text">
                    {prediction.explanation || 'No explanation available'}
                  </p>
                  
                  {prediction.factors && Object.keys(prediction.factors).length > 0 && (
                    <>
                      <h4 className="factors-title">Key Factors</h4>
                      <div className="factors-grid">
                        {Object.entries(prediction.factors).map(([key, value]) => (
                          <div className="factor-chip" key={key}>
                            <span className="factor-key">{key.replace(/_/g, ' ')}</span>
                            <span className="factor-value">{formatFactorValue(value)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                  
                  <div className="model-info">
                    <span className="model-version">
                      Model: {prediction.model_version || 'v1.0'}
                    </span>
                    {prediction.most_likely_outcome && (
                      <span className="most-likely">
                        Most likely: {prediction.most_likely_outcome}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(Predictor);